"use strict";

// Admin view of one QA reviewer's raw activity on one review: their worked
// sessions and the saves they made, for checking a timesheet (for example
// one flagged for time with no edits, or claimed hours that don't match).
import {
    Alert,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";

import { getLanguageName } from "../../utils/generalFunctions";
import { getQAActivity } from "./api";
import { formatQADate } from "./status";

export const ACTION_LABELS = {
    "speech.update": "Edited line",
    "speech.add": "Added line",
    "speech.delete": "Deleted line",
    "speech.approval": "Set line approval",
    "voice.generate": "Generated voice",
    "sfx.generate": "Generated sound effect",
    "speed.adjust": "Changed audio speed",
    "speed.auto": "Auto-fitted audio speed",
    "video.merge": "Merged video",
};

const formatTime = (iso) =>
    iso ? new Date(iso).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" }) : "";

const hours = (minutes) => `${Math.round((minutes / 60) * 100) / 100} h`;

/**
 * `target`: { projectId, userId, name, language, from?, to? }. Without from/to
 * it shows the open cycle (since the reviewer's last timesheet).
 */
const QAActivityDialog = ({ target, onClose }) => {
    const [state, setState] = useState({ loading: true, data: null, error: "" });

    useEffect(() => {
        if (!target) return undefined;
        let cancelled = false;
        setState({ loading: true, data: null, error: "" });
        getQAActivity(target.projectId, target)
            .then(({ data }) => {
                if (!cancelled) setState({ loading: false, data, error: "" });
            })
            .catch((error) => {
                if (!cancelled) {
                    setState({
                        loading: false,
                        data: null,
                        error: error?.response?.data?.error || "Unable to load the activity",
                    });
                }
            });
        return () => {
            cancelled = true;
        };
    }, [target]);

    const { loading, data, error } = state;

    return (
        <Dialog open={Boolean(target)} onClose={onClose} fullWidth maxWidth="md">
            <DialogTitle>
                Activity: {data?.reviewer?.name || target?.name || target?.userId},{" "}
                {target && getLanguageName(target.language)}
            </DialogTitle>
            <DialogContent>
                {loading && (
                    <Stack alignItems="center" sx={{ py: 4 }}>
                        <CircularProgress size={28} />
                    </Stack>
                )}
                {error && <Alert severity="error">{error}</Alert>}
                {data && (
                    <Stack spacing={2}>
                        <Typography variant="body2">
                            {formatQADate(data.from)} – {formatQADate(data.to)}
                        </Typography>
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                            <Chip size="small" label={`${data.totals.sessions} sessions`} />
                            <Chip size="small" label={`${hours(data.totals.sessionMinutes)} in sessions`} />
                            <Chip size="small" label={`${hours(data.totals.watchingMinutes)} watching`} />
                            <Chip size="small" label={`${data.totals.actions} saves`} />
                            {data.totals.failedActions > 0 && (
                                <Chip
                                    size="small"
                                    color="warning"
                                    label={`${data.totals.failedActions} failed`}
                                />
                            )}
                        </Stack>

                        <Typography variant="subtitle2">Sessions</Typography>
                        {data.sessions.length === 0 ? (
                            <Typography variant="body2">No time recorded in this period.</Typography>
                        ) : (
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Start</TableCell>
                                        <TableCell>End</TableCell>
                                        <TableCell>Length</TableCell>
                                        <TableCell>Watching</TableCell>
                                        <TableCell>Active</TableCell>
                                        <TableCell>Minutes with saves</TableCell>
                                        <TableCell>Short gaps</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {data.sessions.map((session) => (
                                        <TableRow key={session.start}>
                                            <TableCell>{formatQADate(session.start)}</TableCell>
                                            <TableCell>{formatTime(session.end)}</TableCell>
                                            <TableCell>{session.minutes} min</TableCell>
                                            <TableCell>{session.watchingMinutes} min</TableCell>
                                            <TableCell>{session.activeMinutes} min</TableCell>
                                            <TableCell>{session.minutesWithActions}</TableCell>
                                            <TableCell>{session.idleGapMinutes} min</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        )}

                        <Typography variant="subtitle2">Saves</Typography>
                        {data.actions.length === 0 ? (
                            <Typography variant="body2">No saves in this period.</Typography>
                        ) : (
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Time</TableCell>
                                        <TableCell>Action</TableCell>
                                        <TableCell>Line</TableCell>
                                        <TableCell>Result</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {data.actions.map((action, index) => (
                                        <TableRow key={`${action.at}-${index}`}>
                                            <TableCell>{formatQADate(action.at)}</TableCell>
                                            <TableCell>{ACTION_LABELS[action.action] || action.action}</TableCell>
                                            <TableCell>{action.targetId || "—"}</TableCell>
                                            <TableCell>
                                                {action.httpStatus >= 500 ? (
                                                    <Chip size="small" color="warning" label="Failed" />
                                                ) : (
                                                    "Saved"
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        )}
                        {data.actionsTruncated && (
                            <Alert severity="info">Showing the first {data.actions.length} saves.</Alert>
                        )}
                    </Stack>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
};

export default QAActivityDialog;
